/**
 * The single contracts store (owner, 27 Sep 2026; LIVE since the switch of 4 Oct 2026): PostgreSQL on the engine's
 * server, built by the engine from Ejar's Contracts Report (engine/api/contract_record.py, contract_store.py) and
 * read through the engine's /api/store (finesService.store*) - shown in the row shapes the pages already use. On
 * when REACT_APP_CONTRACTS_V2=true: .env.production sets it (not in git - every live build must keep it; without
 * it the pages read and write the frozen Firestore archive). engine/SWITCH_TO_POSTGRES.md.
 */
import { finesService } from './finesService';
import { phoneDigits } from '../utils/phoneUtils';

export const USE_CONTRACTS_V2 = process.env.REACT_APP_CONTRACTS_V2 === 'true';

// Every record a page reads from the store is told to the listeners (the contacts memory keeps them, so a phone /
// identity / status looked up by contract number is the one the page already read - no second read).
const recordListeners = new Set();
/** `fn(records)` after every store read; returns the unsubscribe. */
export const onStoreRecords = (fn) => { recordListeners.add(fn); return () => recordListeners.delete(fn); };
const told = (records) => {
    const list = Array.isArray(records) ? records : [];
    if (list.length) recordListeners.forEach((fn) => { try { fn(list); } catch (e) { /* a listener never fails a read */ } });
    return list;
};


const pad = (n) => String(n).padStart(2, '0');

/** Ejar's ISO UTC time as the "dd/mm/yyyy HH:MM" Dubai text the pages parse. */
export const dubaiText = (iso) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const d = new Date(t + 4 * 60 * 60 * 1000);
    return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

const openingComment = (v) => ((v.comments || []).find((c) => c.type === 'opening') || {}).text || '';

const plateKey = (p) => String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * The car the contract has NOW: the engine's `car_now` (its latest "Create Contract" E-Check - Ejar's contract page
 * keeps a swapped contract's FIRST car in `car`; owner, 4 Nov 2026: Mismatch / Switch Back never saw a swap), else
 * `car`. The model is car_now's only when it is another car than `car` (Ejar's list names the model better).
 * `at`: when the contract went on it ('' with no E-Check).
 */
const carNowOf = (v) => {
    const car = v.car || {};
    const now = v.car_now || {};
    if (!plateKey(now.plate) || plateKey(now.plate) === plateKey(car.plate)) {
        return { plate: car.plate || now.plate || '', model: car.model || now.model || '', at: now.at || '' };
    }
    return { plate: now.plate, model: now.model || '', at: now.at || '' };
};

/** A v2 record as the old `contracts` document (what contractRowOf reads). */
export const toLegacyContract = (v) => {
    const now = carNowOf(v);
    // the Invygo car = the one staff typed by hand (Edit Mode / the calculator / a skipped switch back: the engine puts
    // it on the item as invygo_plate / invygo_model, contract_store._with_staff - any kind of contract), else the car an
    // Invygo contract was opened with (its "Create Contract" E-Check). A typed plate never takes another car's model.
    const invygoPlate = v.invygo_plate || (v.kind === 'Invygo' ? (v.first_car?.plate || v.car?.plate || '') : '');
    // the car ANY contract was opened with (owner, 4 Nov 2026: Switch / Switch Back "للكل"): the Monthly table's own
    // car of a contract with no Invygo car (contracts.logic mapFirestoreToLegacy) - its model as Ejar's page names it
    // when it is that car
    const first = v.first_car || {};
    const ownModel = plateKey(first.plate) && plateKey(first.plate) === plateKey(v.car?.plate)
        ? (v.car?.model || first.model || '') : (first.model || '');
    // on another car than its own (typed, else opened with): since when - the switch day of the Switch Back rule
    const own = v.invygo_plate || first.plate || '';
    const switchedAt = plateKey(own) && plateKey(now.plate) !== plateKey(own) ? now.at : '';
    return {
        contract_no: v.contract_no,
        booking_number: v.booking_number || '',
        customer: v.customer?.name || '',
        phone: v.customer?.phone || '',
        pickup_branch: v.branch || '',
        plate_no: now.plate,
        model: now.model,
        // an Invygo contract on another car than its Invygo car: that car and since when (the table's "yelo car · since")
        car_history: switchedAt && v.kind === 'Invygo'
            ? [{ plate: now.plate, model: now.model, from: switchedAt, kind: 'yelo' }] : [],
        car_now_at: switchedAt,                            // '' = on its own car (always sent)
        pickup_date: dubaiText(v.pickup_date),
        dropoff_date: dubaiText(v.dropoff_date),
        status: v.status || '',
        created_by: v.created_by || '',
        // the Ejar booking it was opened from (the engine joins it): where it was made and by whom - "" = none read
        booking_source: v.booking_source || '',
        booking_by: v.booking_by || '',
        opened_by_short: v.opened_by_short || '',          // Bayan, Gween (the engine's staff_names)
        contract_type: v.kind || '',
        invygo_plate: invygoPlate,
        invygo_model: v.invygo_plate ? (v.invygo_model || '') : (v.kind === 'Invygo' ? (v.first_car?.model || v.car?.model || '') : ''),
        opened_plate: first.plate || '',
        opened_model: first.plate ? ownModel : '',
        total_rent: v.total_rent ?? '',
        rate_schedule: v.rate_schedule || [],
        monthly_calculations: v.monthly_calculations || [],
        ejar_opening_comment: openingComment(v),
        // every comment written on Ejar ({at, by, text, type}): the Monthly Calculator shows them beside the staff notes and
        // in each month (owner, 4 Oct 2026)
        ejar_comments: Array.isArray(v.comments) ? v.comments : [],
        last_updated: v.last_updated,
    };
};

/** A v2 record as the old `airport_contracts` row (Ejar's column names). */
export const toLegacyAirport = (v) => ({
    id: v.contract_no,
    contract_no: v.contract_no,
    'Contract No.': v.contract_no,
    'Booking Number': v.booking_number || '',
    'Customer': v.customer?.name || '',
    'Phone Number': phoneDigits(v.customer?.phone || ''),     // one phone format, with the country code
    'Pick-up Branch': v.branch || '',
    'Drop-off Branch': v.return_branch || '',
    'Pick-up Date': dubaiText(v.pickup_date),
    'Drop-off Date': dubaiText(v.dropoff_date),
    'Plate No.': v.car?.plate || '',
    'Model': v.car?.model || '',
    'Status': v.status || '',
    'Customer Type': v.contract_type || '',
    'Contract Type': v.kind || '',
    'Created By': v.created_by || '',
    identity: v.customer?.identity || '',
    last_updated: v.last_updated,
    // new fields, for the pages that start using them
    deposit: v.deposit || null,
    money: v.money || null,
    latest_note: v.latest_note || '',
});

const asContractDoc = (v) => ({ id: v.contract_no, data: () => toLegacyContract(v) });

/** Business Bay + Invygo as doc-like objects for contractRowOf: the OPEN ones (the store serves no whole 5 years to a
 *  browser; a closed contract comes by search or by number). */
export const v2BusinessBayDocs = async () => told(await finesService.storeOpen('monthly')).map(asContractDoc);

/** The open Business Bay + Invygo contracts and the engine's clock at that read (`asOf`; '' from an older engine). */
export const v2MonthlyOpenWithAsOf = async () => {
    const page = await finesService.storeOpenPage('monthly');
    return { docs: told(page.items).map(asContractDoc), asOf: page.asOf || '' };
};

/** The open Business Bay + Invygo contracts only (owner, 27 Sep 2026: the Monthly page loads these). */
export const v2MonthlyOpen = async () => (await v2MonthlyOpenWithAsOf()).docs;

/**
 * Business Bay + Invygo contracts the engine changed since `sinceIso` (open or not - a closed one leaves the page),
 * every page of the answer, and `asOf` of the FIRST page: the engine's clock when the read began. A later page's
 * asOf is later, and a row stamped before it that committed after its own place in the list was read would be
 * skipped by the next read ('' from an older engine).
 */
export const v2MonthlyChangedSinceWithAsOf = async (sinceIso) => {
    const out = [];
    let asOf = null;
    let after = '';
    do {
        const page = await finesService.storeChanges({ group: 'monthly', since: sinceIso, after });
        if (asOf === null) asOf = page.asOf || '';
        out.push(...told(page.items));
        after = page.next;
    } while (after);
    return { docs: out.map(asContractDoc), asOf };
};

/** The same, the contracts only. */
export const v2MonthlyChangedSince = async (sinceIso) => (await v2MonthlyChangedSinceWithAsOf(sinceIso)).docs;

const SERVER_OVERLAP_MS = 2 * 60000;    // a row stamped just before asOf can commit just after it
const BROWSER_OVERLAP_MS = 5 * 60000;   // the old start, only for an engine that sends no asOf

/** An ISO time as ms, or null. Digits past the milliseconds are cut: not every browser reads 6 of them. */
const isoMs = (iso) => {
    if (!iso) return null;
    const t = new Date(String(iso).replace(/(\.\d{3})\d+/, '$1')).getTime();
    return Number.isNaN(t) ? null : t;
};

/**
 * Where the next "changed since" read starts (review, 27 Sep 2026: it was the browser's save time minus 5 min, so a
 * browser clock more than 5 min fast missed changes until the 24 h whole read): the engine's own `asOf` of the last
 * good read, minus 2 min. From an older engine (no asOf) the old start, the browser's `savedAt` minus 5 min.
 * '' when neither is a time - then the caller reads whole.
 */
export const changesSinceOf = (asOf, savedAt) => {
    const server = isoMs(asOf);
    if (server !== null) return new Date(server - SERVER_OVERLAP_MS).toISOString();
    const saved = isoMs(savedAt);
    return saved === null ? '' : new Date(saved - BROWSER_OVERLAP_MS).toISOString();
};

// --- Search: the same words the engine writes on every contract as `search: {word: true}` (contract_record.py) ---
const AR = { 'أ': 'ا', 'إ': 'ا', 'آ': 'ا', 'ٱ': 'ا', 'ة': 'ه', 'ى': 'ي' };
const NAME_PREFIX_MIN = 3;
const NAME_PREFIX_MAX = 12;
const GULF = /^(971|966|965|973|974|968)(\d{7,})$/;

/** Lower case, one form of alef / ta marbuta / ya, letters and digits only (engine: search_norm). */
export const searchNorm = (text) => String(text ?? '').toLowerCase().replace(/[أإآٱةى]/g, (c) => AR[c])
    .replace(/[^a-z0-9؀-ۿ]/g, '');

/**
 * What staff typed as groups of words to look up, one query per group: first the whole text (a contract /
 * booking / plate / identity), its digits as a phone with and without the country code, and the longest name
 * word; then each other name word (up to 2) on its own, so a typo in one word of a name still finds the
 * contract by another (e.g. "Omar Abdulraham" finds Omar Abdulrahman by "omar"). Name words are cut at 12
 * letters, as the engine stores them. The page's own filter then keeps only the rows that match everything
 * typed. At most 10 words a group (with the 2 branches that is 20 of Firestore's 30 OR terms).
 */
export const searchTokenGroupsOf = (text) => {
    const raw = String(text ?? '').trim();
    const first = [];
    const whole = searchNorm(raw);
    if (whole.length >= NAME_PREFIX_MIN) first.push(whole);
    if (!/[a-z؀-ۿ]/i.test(raw)) {           // no letters (Latin or Arabic): digits of a phone / number
        const digits = raw.replace(/\D/g, '').replace(/^0+/, '');
        const gulf = digits.match(GULF);
        if (gulf) first.push(gulf[2].replace(/^0+/, ''));
        if (digits.length >= 4) first.push(digits);
    }
    const words = [...new Set(raw.toLowerCase().split(/\s+/).map(searchNorm)
        .filter((w) => w.length >= NAME_PREFIX_MIN).map((w) => w.slice(0, NAME_PREFIX_MAX)))]
        .sort((a, b) => b.length - a.length);
    if (words[0]) first.push(words[0]);
    const clean = (g) => [...new Set(g)].filter((t) => t.length >= NAME_PREFIX_MIN).slice(0, 10);
    return [clean(first), ...words.slice(1, 3).filter((w) => !first.includes(w)).map((w) => [w])].filter((g) => g.length);
};

/** Every word searchTokenGroupsOf looks up, flat. */
export const searchTokensOf = (text) => [...new Set(searchTokenGroupsOf(text).flat())];

/** Business Bay + Invygo contracts (open or closed, any year) found by what staff typed - the engine searches
 *  (exact words, the text anywhere, a slightly misspelt name). */
export const v2MonthlySearch = async (text) => told(await finesService.storeSearch({ q: text, group: 'monthly' })).map(asContractDoc);

/** The engine's by-ids looks up at most this many numbers a call (store_routes.MAX_IDS); the rest were not read. */
export const BY_IDS_MAX = 1000;

/**
 * Store records by contract number, BY_IDS_MAX numbers a call, so a longer list is read whole and not cut at the
 * engine's cap (review, 27 Sep 2026). A number sent twice (the engine's key: trimmed, upper case) is asked once, or
 * two calls would each return its row; a blank one is not sent (the engine skips it). Each number goes as it came.
 */
const storeByIdsChunked = async (contractIds, group) => {
    const seen = new Set();
    const ids = (contractIds || []).filter((c) => {
        const key = String(c ?? '').trim().toUpperCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    const out = [];
    for (let i = 0; i < ids.length; i += BY_IDS_MAX) {
        out.push(...told(await finesService.storeByIds({ ids: ids.slice(i, i + BY_IDS_MAX), group })));
    }
    return out;
};

/** Business Bay + Invygo contracts by number, as doc-like objects (others are skipped). */
export const v2MonthlyByIds = async (contractIds) => (await storeByIdsChunked(contractIds, 'monthly')).map(asContractDoc);

/** Airport contracts by number, as airport rows (others are skipped). */
export const v2AirportByIds = async (contractIds) => (await storeByIdsChunked(contractIds, 'airport')).map(toLegacyAirport);

/** Terminal 1 + 2 as airport rows: the OPEN ones (never ~91k over 5 years to a browser). */
export const v2AirportRows = async () => told(await finesService.storeOpen('airport')).map(toLegacyAirport);

/** The open airport contracts only (tens, not tens of thousands). */
export const v2AirportOpen = async () => told(await finesService.storeOpen('airport')).map(toLegacyAirport);

/**
 * One page of closed airport contracts, newest return first, then the ones with no drop-off; at most `limitCount`;
 * `done` at the end. The next page starts from the `cursor` this returned, given back as `cursor`: it goes to the
 * engine as it came, as `before`. The caller keeps it (review, 27 Sep 2026): the Airport page used to give back
 * only `nextOffsetDays`, which this file turned back into the cursor from a map in memory - gone whenever this
 * module loads again, and then the page started from a time instead.
 * `nextOffsetDays` is how many days back this page reached (the oldest return in it), for the page's "loaded
 * since" only. A number with no cursor (a caller that kept only that) is the old start, "returned more than that
 * many days ago".
 */
export const v2AirportClosedPage = async ({ fromOffsetDays = 0, cursor, limitCount = 500 } = {}) => {
    const day = 24 * 60 * 60 * 1000;
    // the first page sends no `before`: "now" on the engine's clock, not the browser's
    const before = cursor || (fromOffsetDays > 0 ? new Date(Date.now() - fromOffsetDays * day).toISOString() : undefined);
    const page = (await finesService.storeClosed({ group: 'airport', before, limit: limitCount })) || {};
    const items = told(page.items);
    const oldest = items.length ? isoMs(items[items.length - 1].dropoff_date) : null;
    // never less than asked for: a page of contracts with no drop-off does not move "loaded since" forward
    const nextOffsetDays = Math.max(oldest === null ? 0 : (Date.now() - oldest) / day, fromOffsetDays);
    // The engine's exact `cursor` ("<time>|<contract>", or "|<contract>" among the ones with no drop-off; null at the
    // end) whenever the engine sends one. Only an older engine without it: its `next`, the last row's drop-off time -
    // the rows of that minute come again (the page drops repeats by number), and it is null once the page reaches
    // the contracts with no drop-off.
    const nextKey = ('cursor' in page ? page.cursor : page.next) || null;
    return { rows: items.map(toLegacyAirport), nextOffsetDays, cursor: nextKey, done: !nextKey };
};

export const isOpenRow = (r) => String(r.Status || r.status || '').trim().toLowerCase() === 'open';

// --- Every other reader of contract data (27 Sep 2026: "we will not rely on Firebase anymore") ---------------------
// Ejar's branch ids of the store (engine contract_store.GROUPS): Terminal 1 + 2 are the airport, the rest monthly.
const AIRPORT_BRANCH_IDS = new Set([36, 174]);

/** An airport contract (Terminal 1 / 2), by Ejar's branch id - else by the branch name. */
export const isAirportRecord = (v) => (v && v.branch_id != null && v.branch_id !== ''
    ? AIRPORT_BRANCH_IDS.has(Number(v.branch_id))
    : /airport|terminal/i.test(String((v && (v.branch || v.ejar_branch_name)) || '')));

/** A store record as the document its old collection held: an airport row (Ejar's column names), else the
 *  `contracts` document (snake_case) with its `id` - the two shapes the matcher and the lookups read. */
export const toLegacyDoc = (v) => (isAirportRecord(v) ? toLegacyAirport(v) : { id: v.contract_no, ...toLegacyContract(v) });

/** A store record as a contacts row ({ c, n, p, i, b, d, e, s, t } - contactsService). */
export const contactRowOfRecord = (v) => ({
    c: String(v.contract_no || '').trim().toUpperCase(),
    n: String(v.customer?.name || '').trim(),
    p: phoneDigits(v.customer?.phone || ''),
    i: String(v.customer?.identity || '').trim(),
    b: String(v.branch || v.ejar_branch_name || '').trim(),
    d: dubaiText(v.pickup_date),
    e: dubaiText(v.dropoff_date),
    s: String(v.status || '').trim(),
    t: String(v.contract_type || '').trim(),
    // Ejar's customer number (the contract's ContractList row): THE customer (owner, 4 Oct 2026 - one customer showed a
    // passport on one contract and a Saudi ID on the next; Ejar keeps both under one number)
    u: ejarCustomerOf(v),
});

/** Ejar's customer number of a store record (its ejar_facts), as digits; '' when Ejar's list row was not read yet. */
export const ejarCustomerOf = (v) => {
    const id = String(v?.ejar_facts?.customer_id ?? '').trim();
    return /^\d{1,18}$/.test(id) ? id : '';
};

/** Every store record of one Ejar customer number (any group unless `group` says). */
export const v2CustomerRecords = async (customerId, group = 'all') => {
    const id = String(customerId ?? '').trim();
    return /^\d{1,18}$/.test(id) ? told(await finesService.storeByCustomer({ customerId: id, group })) : [];
};

/** Store records by contract number (any group unless `group` says), BY_IDS_MAX a call. */
export const v2RecordsByIds = (contractIds, group = 'all') => storeByIdsChunked(contractIds, group);

/** Store records found by what staff typed (the engine's search: exact words, the text anywhere, a near name). */
export const v2SearchRecords = async (text, group = 'all') => {
    const q = String(text ?? '').trim();
    return q ? told(await finesService.storeSearch({ q, group })) : [];
};

const sameKey = (a, b) => String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();

/** The records whose contract number or booking number IS the text (never a near match: a mistyped number is not
 *  found) - what the old reads by document id / `booking_number ==` returned. */
export const v2ExactRecords = async (text, group = 'all') => {
    const q = String(text ?? '').trim();
    if (!q) return [];
    return (await v2SearchRecords(q, group)).filter((v) => sameKey(v.contract_no, q) || sameKey(v.booking_number, q));
};

/** The open contracts of a group (monthly | airport | all), as records. */
export const v2OpenRecords = async (group = 'all') => told(await finesService.storeOpen(group));

const CLOSED_PAGE = 1000;
const CLOSED_MAX_PAGES = 200;          // 200k contracts: never a loop without end
const CLOSED_KEEP_MS = 60 * 1000;      // one read serves the calls of the next minute (a page's parallel reads)
const closedReads = new Map();         // group -> [{ since, at, promise }]

/**
 * The CLOSED contracts of a group returned at or after `sinceMs` (a time in ms), newest return first: the store's
 * closed pages (by drop-off, 1000 a page, the engine's cursor) until a page reaches an older return. What a page
 * that shows a period needs (the contracts that ran in it are open, or were returned after its start) - never the
 * whole five years. A read of the last minute that started earlier serves a later start.
 */
export const v2ClosedSince = async (group, sinceMs) => {
    const since = Number(sinceMs);
    if (sinceMs === null || sinceMs === undefined || !Number.isFinite(since)) throw new Error('v2ClosedSince: a start time is needed');
    const now = Date.now();
    const kept = (closedReads.get(group) || []).filter((r) => now - r.at < CLOSED_KEEP_MS);
    closedReads.set(group, kept);
    const inTime = (v) => { const t = isoMs(v.dropoff_date); return t !== null && t >= since; };
    const hit = kept.find((r) => r.since <= since);
    if (hit) return (await hit.promise).filter(inTime);
    const promise = (async () => {
        const out = [];
        let before;
        for (let page = 0; page < CLOSED_MAX_PAGES; page += 1) {
            const answer = (await finesService.storeClosed({ group, before, limit: CLOSED_PAGE })) || {};
            const items = told(answer.items);
            const fresh = items.filter(inTime);
            out.push(...fresh);
            const next = ('cursor' in answer ? answer.cursor : answer.next) || null;
            // newest return first, the ones with no drop-off last: a page that reaches an older return is the end
            if (!next || !items.length || fresh.length < items.length) break;
            before = next;
        }
        return out;
    })();
    const entry = { since, at: now, promise };
    kept.push(entry);
    promise.catch(() => { closedReads.set(group, (closedReads.get(group) || []).filter((r) => r !== entry)); });
    return promise;
};

/** Forget the closed reads kept for a minute (a Refresh reads again). */
export const forgetClosedReads = () => closedReads.clear();

/** Dubai midnight of a "yyyy-mm-dd" day, in ms (null when it is no day). */
export const dubaiDayStartMs = (yyyyMmDd) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yyyyMmDd || '').trim());
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) - 4 * 60 * 60 * 1000 : null;
};

const openAndClosedSince = async (group, sinceDay) => {
    const [open, closed] = await Promise.all([v2OpenRecords(group), v2ClosedSince(group, dubaiDayStartMs(sinceDay))]);
    return [...new Map([...closed, ...open].map((v) => [v.contract_no, v])).values()];
};

/** The Business Bay + Invygo contracts open at or after `sinceDay` ("yyyy-mm-dd", Dubai): the open ones and the
 *  ones returned since, as doc-like objects for contractRowOf. */
export const v2MonthlyDocsSince = async (sinceDay) => (await openAndClosedSince('monthly', sinceDay)).map(asContractDoc);

/** The same for Terminal 1 + 2, as airport rows. */
export const v2AirportRowsSince = async (sinceDay) => (await openAndClosedSince('airport', sinceDay)).map(toLegacyAirport);

/**
 * The airport contracts of each day ("dd/mm/yyyy", Dubai) as the old per-day reads gave them: `[picked up that day,
 * drop-off that day]` for every day asked - any status, an open one by its planned drop-off (the page keeps the
 * closed ones). One read for all the days: the open contracts and the ones returned since the first day.
 */
export const v2AirportByDays = async (days) => {
    const asked = (days || []).map((d) => String(d || '').trim());
    const valid = asked.filter((d) => /^\d{2}\/\d{2}\/\d{4}$/.test(d));
    if (!valid.length) return asked.map(() => [[], []]);
    const first = valid.map((d) => `${d.slice(6, 10)}-${d.slice(3, 5)}-${d.slice(0, 2)}`).sort()[0];
    const rows = await v2AirportRowsSince(first);
    const dayOf = (text) => String(text || '').slice(0, 10);
    return asked.map((d) => [rows.filter((r) => dayOf(r['Pick-up Date']) === d), rows.filter((r) => dayOf(r['Drop-off Date']) === d)]);
};
